/* eslint-disable react-hooks/exhaustive-deps */
'use client';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useEffectOnce } from '@utils/react/hooks';
import { getDevices } from '@utils/graphql/creators/devices';
import { getMetadata } from '@utils/graphql/creators/metadata';
import {
  Button, Modal, Table, ActionModal,
} from '@skyline-platform/alchemy';
import { useMessageEmitter } from '@utils/react/eventEmitterContext';
import { tabEventEmitter } from '../utils/TabEventEmitters';
import '../page.css';
import { CameraForm } from '@components/FormComponents/CameraForm/CameraForm';
import { SearchBarComponent } from '@components/SearchBar/SearchBarComponent';
import { CameraActions } from './CameraActions';
import {
  normalizeKeys,
  renderBadgeForCameraStatus,
  handleTableEvent,
} from '../utils/helpers';
import {
  buildCombinedCriteria,
  buildSearchCriteria,
  normalizeStringFields,
} from '../../utils/helpers';
import { useLocation } from 'react-router-dom';
import { getLogger } from '@utils/general';
import { CameraBulkActions } from '@components/BulkActions/CameraBulkActions';
import PropTypes from 'prop-types';
import { SkipToMainContent } from '@components/Misc';
import { dedupeRowsById } from '../utils/eventHelpers';
import { useServerPagination } from '@utils/react/hooks/useServerPagination';
import { transformMetadataCriteria, transformStatusCriteria } from './criteriaUtils';
import { getConfig } from '@utils/config';

const logger = getLogger('src.pages.split.cameras');

const DEVICE_FETCH_FIELDS = [
  'id',
  'name',
  'description',
  'latitude',
  'longitude',
  'metadata',
  'streams.id',
  'streams.name',
  'streams.online',
  'problem_camera',
  'network_address',
  'type',
  'connection_type',
  'elevation',
  'username',
  'has_creds',
  'sources.id',
  'sources.category',
];

const DEVICE_FIELDS_FOR_DELETE = [
  'id',
  'name',
  'streams.id',
  'sources.id',
];
// Maps frontend search field names to backend GraphQL criteria keys.
const SEARCH_FIELD_MAP = {
  cameraName: 'name',
  description: 'description',
  network_address: 'network_address',
  latitude: 'latitudeText',
  longitude: 'longitudeText',
};

// Maps table column field names to backend sort field names.
// Columns not in this map (offlineStreams) are derived and can't be server-sorted.
const SORT_FIELD_MAP = {
  offlineStreams: 'status',
  network_address: 'network_address',
  connection_type: 'connection_type',
  jurisdiction: 'jurisdiction',
  latitude: 'latitude',
  longitude: 'longitude',
};

// Maps filterable table column field names to backend criteria keys.
// status: N = number of offline streams. status:0 = Online, status:1+ = Offline.
const FILTER_FIELD_MAP = {
  offlineStreams: 'status',
  ...(getConfig().JURISDICTION_FILTER_ENABLED === 'true' ? { jurisdiction: 'jurisdiction' } : {}),
  ...(getConfig().CAMERA_CONNECTION_TYPE_ENABLED === 'true' ? { connection_type: 'connection_type' } : {}),
};

export const AllCameras = ({ userIsAdmin }) => {
  const searchOptions = [
    { key: 'Camera Name', value: 'cameraName' },
    { key: 'Description', value: 'description' },
    { key: 'Network Address', value: 'network_address' },
    { key: 'Latitude', value: 'latitude' },
    { key: 'Longitude', value: 'longitude' },
  ];
  const [selectedFields, setSelectedFields] = useState(
    searchOptions.map((option) => option.value)
  );
  const [selectedCameraId, setSelectedCameraId] = useState();
  const [newCamera, setNewCamera] = useState(null);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  // Stores full {id, name} objects fetched when the user clicks "select all" across all pages.
  // Cleared whenever individual row selection changes so bulk actions always use the right set.
  const [allSelectedRows, setAllSelectedRows] = useState([]);
  const [jurisdictionOptions, setJurisdictionOptions] = useState([]);

  const jurisdictionFilterEnabled = getConfig().JURISDICTION_FILTER_ENABLED === 'true';
  const connectionTypeEnabled = getConfig().CAMERA_CONNECTION_TYPE_ENABLED === 'true';

  useEffect(() => {
    if (!jurisdictionFilterEnabled) return;
    let cancelled = false;
    async function fetchJurisdictions() {
      try {
        const { execute } = await getMetadata({ key: 'regex:jurisdiction' });
        await execute()
          .then(({ error, data }) => {
            if (error) {
              logger.error(error);
            }
            if (!cancelled && data && data.length > 0) {
              const uniqueOptions = Array.from(
                new Set(data.map((j) => j.value).filter(Boolean))
              )
                .sort()
                .map((value) => ({ label: value, value }));
              setJurisdictionOptions(uniqueOptions);
            }
          })
          .catch(() => {
            logger.error(
              `There was a failure retrieving data for zone: ${name}`
            );
          });
      } catch (err) {
        logger.error('Failed to fetch jurisdictions', err);
      }
    }
    fetchJurisdictions();
    return () => { cancelled = true; };
  }, []);

  const mapTableData = useCallback((cameras) => {
    return cameras.map((camera) => {
      let metadata = {};
      if (Array.isArray(camera.metadata)) {
        camera.metadata.forEach(({ key, value }) => {
          metadata[key] = value;
        });
      } else if (camera.metadata) {
        metadata = JSON.parse(camera.metadata);
      }
      const offlineStreamsCount = Array.isArray(camera.streams)
        ? camera.streams.filter((stream) => stream.online === false).length
        : 0;
      const lowercaseMetadataKeys = normalizeKeys(metadata);
      const cameraNormalized = normalizeStringFields(camera, ['description']);
      return {
        ...cameraNormalized,
        jurisdiction: lowercaseMetadataKeys['jurisdiction'] || '',
        connection_type: camera.connection_type || '',
        offlineStreams: offlineStreamsCount,
        action: { ...camera },
      };
    });
  }, []);

  const {
    rows,
    totalRows,
    loading,
    currentPage,
    rowsPerPage,
    handleSort,
    sortDirection,
    handlePageChange,
    resetToPage,
    handleRowsPerPageChange,
    handleSearch,
    clearSearch,
    refetchCurrentPage,
    fetchAll,
    setRows,
    paginationResetKey,
  } = useServerPagination({
    fetchFn: getDevices,
    mapFn: mapTableData,
    fetchFields: DEVICE_FETCH_FIELDS,
    defaultSort: { field: 'name', direction: 'asc' },
    sortFieldMap: SORT_FIELD_MAP,
    enableSort: true,
    extraOptions: { includePrivateMetadata: true },
    onError: (error) => logger.error(error),
  });

  const nestedUpdateTimersRef = useRef(new Map());
  const rowsRef = useRef(rows);
  // Tracks full row objects for individually selected rows across all pages.
  // A new Map reference is created on each change so React detects the update.
  const [selectedRowObjects, setSelectedRowObjects] = useState(new Map());
  // Keeps a ref to allSelectedRows so handleSelectionChange can read the
  // previous select-all data when transitioning to individual mode.
  const allSelectedRowsRef = useRef([]);
  // Tracks the ID of a camera we just created via the form so that the concurrent
  // WebSocket create event can be suppressed (we handle the fetch ourselves).
  const pendingNewCameraIdRef = useRef(null);
  // Ensures the deep-link ?id= param is only processed once on initial load.
  // Without this, the effect would re-fire on every rows update (pagination,
  // sort, search, WebSocket events) and re-open the tab repeatedly.
  const deepLinkHandledRef = useRef(false);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    allSelectedRowsRef.current = allSelectedRows;
  }, [allSelectedRows]);

  const tableContext = {
    rowsRef,
    setRows,
    setSelectedRowIds,
    setSelectedId: setSelectedCameraId,
  };

  const applyCameraRows = useCallback((newRows) => {
    const normalizedRows = dedupeRowsById(newRows);
    setRows(normalizedRows);
  }, [setRows]);

  // Prefetch all IDs in the background whenever criteria/sort changes so that
  // clicking select-all is instant. The cache is keyed to fetchAll's identity
  // (which changes whenever criteria, sort, or fetchFn changes).
  const selectAllCacheRef = useRef(null);
  useEffect(() => {
    // Skip the background prefetch while navigating to a newly created item —
    // the fetchAll in the newCamera effect is already in-flight.
    if (pendingNewCameraIdRef.current) return;
    selectAllCacheRef.current = null; // invalidate on any fetchAll change
    let cancelled = false;
    fetchAll(DEVICE_FIELDS_FOR_DELETE).then((all) => {
      if (!cancelled) selectAllCacheRef.current = all;
    });
    return () => { cancelled = true; };
  }, [fetchAll]);

  const handleSelectAll = useCallback(async () => {
    const all = selectAllCacheRef.current ?? await fetchAll(DEVICE_FIELDS_FOR_DELETE);
    setSelectedRowObjects(new Map()); // individual selection no longer relevant
    setAllSelectedRows(all);
    setSelectedRowIds(all.map((r) => r.id));
  }, [fetchAll]);

  const handleSelectionChange = useCallback((ids) => {
    const idSet = new Set(ids.map(String));
    setSelectedRowObjects((prev) => {
      // When transitioning from select-all to individual mode the Map is empty
      // but allSelectedRowsRef still holds {id, name} for every record.
      // Seed the Map so cross-page selections aren't lost.
      const next = prev.size === 0 && allSelectedRowsRef.current.length > 0
        ? new Map(allSelectedRowsRef.current.map((r) => [String(r.id), r]))
        : new Map(prev);
      for (const id of next.keys()) {
        if (!idSet.has(id)) next.delete(id);
      }
      for (const row of rowsRef.current ?? []) {
        if (idSet.has(String(row.id))) next.set(String(row.id), row);
      }
      return next;
    });
    setSelectedRowIds(ids);
    setAllSelectedRows([]);
  }, []);

  const hasActiveCriteriaRef = useRef(false);
  const activeFiltersRef = useRef({});
  const [activeFiltersState, setActiveFiltersState] = useState({});
  const lastSearchCriteriaRef = useRef({});

  const applyCombinedCriteria = useCallback((searchCriteria, columnFilters) => {
    const combined = transformMetadataCriteria(
      transformStatusCriteria(
        buildCombinedCriteria(searchCriteria, columnFilters, FILTER_FIELD_MAP)
      )
    );
    const hasCriteria = Object.keys(combined).length > 0;
    if (hasCriteria) {
      hasActiveCriteriaRef.current = true;
      handleSearch(combined);
    } else if (hasActiveCriteriaRef.current) {
      hasActiveCriteriaRef.current = false;
      clearSearch();
    }
  }, [handleSearch, clearSearch]);

  const handleServerSearch = useCallback((searchTerm, fields) => {
    if (!searchTerm) {
      lastSearchCriteriaRef.current = {};
      applyCombinedCriteria({}, activeFiltersRef.current);
      return;
    }
    const criteria = buildSearchCriteria(searchTerm, fields, SEARCH_FIELD_MAP);
    if (Object.keys(criteria).length === 0) return;
    lastSearchCriteriaRef.current = criteria;
    applyCombinedCriteria(criteria, activeFiltersRef.current);
  }, [applyCombinedCriteria]);

  const handleFilterChangeFromTable = useCallback((columnFilters) => {
    activeFiltersRef.current = columnFilters;
    setActiveFiltersState(columnFilters);
    applyCombinedCriteria(lastSearchCriteriaRef.current, columnFilters);
  }, [applyCombinedCriteria]);

  const location = useLocation();
  // Handle navigating from Streams page with selected Camera
  useEffect(() => {
    if (rows.length === 0 || deepLinkHandledRef.current) return;
    const params = new URLSearchParams(location.search);
    const cameraId = params.get('id');
    if (!cameraId) return;
    deepLinkHandledRef.current = true;
    tabEventEmitter.emit('updateTabs', cameraId);
    const navigateToCamera = async () => {
      const all = selectAllCacheRef.current ?? await fetchAll(['id']);
      const index = all.findIndex((r) => String(r.id) === String(cameraId));
      if (index >= 0) {
        const targetPage = Math.ceil((index + 1) / rowsPerPage);
        if (targetPage !== currentPage) {
          resetToPage(targetPage);
        }
      }
      setTimeout(() => setSelectedCameraId(cameraId), 0);
    };
    navigateToCamera();
  }, [rows, location.search]);

  const handleSelectedTab = (eventObject) => {
    const cameraId = eventObject.detail;
    setSelectedCameraId(cameraId);
  };

  const handleEvent = async (eventMessage) => {
    // AllCameras uses handleTableEvent because its rows are affected by multiple model types:
    // a stream status change or source update on a nested camera stream needs to ripple into
    // the camera row that owns it. handleTableEvent handles that fan-out via resolveParentId.
    // Pages whose events only ever affect their own rows (AllZones, AllAppliances, AllApiKeys)
    // use inline patchRow/removeRow/addRow instead, which is simpler for that case.
    //
    // Guard: only process models this page displays or nests. Appliance
    // connection_count storms (hundreds of events/min) must not trigger table work.
    const model = eventMessage?.detail?.payload?.model?.toLowerCase();
    if (model && ![
      'device',
      'stream',
      'source',
    ].includes(model)) return;
    // Suppress the WebSocket create event for a camera we just created via the
    // form — the newCamera effect handles fetching the correct page itself.
    const parsed = eventMessage?.detail?.payload ? JSON.parse(eventMessage.detail.payload.data ?? '{}') : null;
    if (
      pendingNewCameraIdRef.current &&
      eventMessage?.detail?.payload?.action?.toLowerCase() === 'create' &&
      eventMessage?.detail?.payload?.model?.toLowerCase() === 'device' &&
      String(parsed?.current?.id) === String(pendingNewCameraIdRef.current)
    ) {
      return;
    }
    await handleTableEvent(eventMessage, {
      sourceModel: 'device',
      fetchFields: DEVICE_FETCH_FIELDS,
      mapTableData,
      tableContext,
      applyRows: applyCameraRows,
      nestedUpdateTimersRef,
      buildFetchPatch: (fetched) => ({
        rowPatch: { username: fetched.username, has_creds: fetched.has_creds },
        actionPatch: { username: fetched.username, has_creds: fetched.has_creds },
      }),
      resolveParentId: (model, linkedEntityId, currentRows) => {
        const fieldName = `${model}s`;
        return (
          currentRows?.find((row) =>
            row.action?.[fieldName]?.some(
              (s) => String(s.id) === String(linkedEntityId)
            )
          )?.id ?? null
        );
      },
    });
  };

  /***** SUBSCRIPTIONS *****/
  const messageHandler = useMessageEmitter();
  useEffectOnce(() => {
    messageHandler.subscribe(handleEvent);

    return () => {
      messageHandler.unsubscribe(handleEvent);
    };
  });

  useEffectOnce(() => {
    tabEventEmitter.on('selectTab', handleSelectedTab);

    return () => {
      tabEventEmitter.off('selectTab', handleSelectedTab);
    };
  });
  /***** END SUBSCRIPTIONS *****/

  useEffect(() => {
    const run = async () => {
      if (newCamera) {
        tabEventEmitter.emit('updateTabs', newCamera.id);
        setSelectedCameraId(newCamera.id);
        pendingNewCameraIdRef.current = newCamera.id;
        // Retry with increasing delays — the backend may not have indexed the new
        // record immediately. Starting at 300ms avoids a wasted instant call.
        let all = [];
        let index = -1;
        const delays = [
          300,
          600,
          1200,
        ];
        for (let attempt = 0; attempt < delays.length && index === -1; attempt++) {
          await new Promise((r) => setTimeout(r, delays[attempt]));
          all = await fetchAll(['id']);
          index = all.findIndex((r) => String(r.id) === String(newCamera.id));
        }
        if (index >= 0) {
          const targetPage = Math.ceil((index + 1) / rowsPerPage);
          if (targetPage !== currentPage) {
            handlePageChange(targetPage);
          } else {
            refetchCurrentPage();
          }
        } else {
          refetchCurrentPage();
        }
        pendingNewCameraIdRef.current = null;
      }
    };
    run();
  }, [newCamera]);

  const allColumns = useMemo(() => [
    {
      field: 'offlineStreams',
      headerName: 'Status',
      cell: (row) => renderBadgeForCameraStatus(row?.offlineStreams),
      width: '130px',
      selector: (row) => row.offlineStreams,
      sortable: true,
      filterable: true,
      filterOptions: [{ label: 'Online', value: 0 }, { label: 'Offline', value: 1 }],
      filterMatcher: (count, selectedValues) => selectedValues.some((v) => (v === 0 ? count === 0 : count > 0)),
    },
    {
      field: 'name',
      headerName: 'Camera Name',
      cell: (row) => (
        <a
          key={`${row.id}-${row.__selected ? 'selected' : 'normal'}`}
          tabIndex={0}
          aria-label={`View details for camera ${row?.name}`}
          className={`camera-name ${row.__selected ? 'selected-tab' : ''}`}
          id={`datacell-${row?.id || Date.now()}`}
          title={row?.name}
          onClick={() => {
            tabEventEmitter.emit('updateTabs', row?.id);
            setSelectedCameraId(row?.id);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              tabEventEmitter.emit('updateTabs', row?.id);
              setSelectedCameraId(row?.id);
              event.preventDefault();
            }
          }}
        >
          {row?.name}
        </a>
      ),
      grow: 1,
      sortable: true,
    },
    {
      field: 'description',
      headerName: 'Description',
      type: 'string',
      grow: 1,
      sortable: true,
    },
    {
      field: 'network_address',
      headerName: 'Network Address',
      type: 'string',
      grow: 1,
      sortable: true,
    },
    ...(connectionTypeEnabled ? [
      {
        field: 'connection_type',
        headerName: 'Connection Type',
        type: 'string',
        grow: 1,
        sortable: true,
        filterable: true,
        filterOptions: [
          { label: 'Radio', value: 'Radio' },
          { label: 'Cellular', value: 'Cellular' },
          { label: 'Fiber', value: 'Fiber' },
        ],
        filterMatcher: () => true,
      },
    ] : []),
    {
      field: 'latitude',
      headerName: 'Latitude',
      type: 'number',
      grow: 0.8,
      sortable: true,
    },
    {
      field: 'longitude',
      headerName: 'Longitude',
      type: 'number',
      grow: 0.8,
      sortable: true,
    },
    {
      field: 'jurisdiction',
      headerName: 'Jurisdiction',
      type: 'string',
      grow: 1,
      sortable: true,
      filterable: jurisdictionFilterEnabled,
      filterOptions: jurisdictionFilterEnabled ? jurisdictionOptions : [],
    },
    {
      field: 'action',
      headerName: '',
      cell: (row) =>
        userIsAdmin ? (
          <ActionModal
            className="camera-row-actions"
            id={row.id + row.name}
            title="Toggle Options"
            content={CameraActions({
              rowInfo: row.action,
              id: row.id + row.name,
            })}
          />
        ) : null,
      width: '50px',
      sortable: false,
    },
  ], [jurisdictionOptions, connectionTypeEnabled]);

  const tableColumns = useMemo(() => {
    return userIsAdmin
      ? allColumns
      : allColumns.filter((col) => col.field !== 'action');
  }, [selectedCameraId, allColumns]);

  // When select-all was used, allSelectedRows has the full cross-page set from fetchAll.
  // For individual checkbox selection, selectedRowObjects accumulates row objects as the
  // user selects across pages — rows alone can't be used because it only contains the current page.
  const selectedCameras = useMemo(() => {
    if (allSelectedRows.length > 0) return allSelectedRows;
    return [...selectedRowObjects.values()];
  }, [allSelectedRows, selectedRowObjects]);

  const displayRows = useMemo(() => {
    return Array.isArray(rows)
      ? rows.map((item) => ({
        ...item,
        __selected: item.id === selectedCameraId,
      }))
      : [];
  }, [rows, selectedCameraId]);

  return (
    <gar-all-cameras>
      <div className="actions-bar">
        {userIsAdmin && (
          <>
            <ActionModal
              id="camerasBulkAction"
              triggerElement={
                <div id="bulk-action-btn" className="bulk-action-btn">
                  Bulk Actions
                  <i aria-hidden={true} className="fa fa-angle-down" />
                </div>
              }
              content={
                <CameraBulkActions
                  cameras={rows}
                  selectedCams={selectedCameras}
                />
              }
              closeOntab={false}
            />
          </>
        )}
        <SearchBarComponent
          searchOptions={searchOptions}
          selectedFields={selectedFields}
          setSelectedFields={setSelectedFields}
          fieldName='Cameras'
          onSearch={handleServerSearch}
        />
        {userIsAdmin && (
          <>
            <Modal
              className="new-camera-modal"
              closeIcon
              header="Create New Camera"
              trigger={({ handleOpen }) => (
                <Button
                  label="New Camera"
                  iconLeft="fa-solid fa-circle-plus"
                  state="default"
                  type="filled"
                  onClick={handleOpen}
                />
              )}
              slots={{
                content: (handleClose) => (
                  <CameraForm
                    handleClose={handleClose}
                    setFormData={setNewCamera}
                  />
                ),
              }}
            />
          </>
        )}
      </div>
      <SkipToMainContent
        text="Skip to pagination controls"
        targetSelector="alc-pagination-bar"
      />
      <div>
        <Table
          key={paginationResetKey}
          tableName='Cameras'
          columns={tableColumns}
          getRowId={(row) => row.id}
          hideInColumnMenu={['action']}
          rows={displayRows}
          style={{
            height: displayRows.length === 0 ? '400px' : undefined,
          }}
          initialSortField="name"
          defaultSortAsc={true}
          sortServer
          onSort={handleSort}
          onFilterChange={handleFilterChangeFromTable}
          defaultColumnFilters={activeFiltersState}
          paginationServer
          paginationTotalRows={totalRows}
          currentPage={currentPage}
          paginationPerPage={rowsPerPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          progressPending={loading}
          sortDirection={sortDirection}
          selectedId={selectedCameraId}
          checkboxSelection={userIsAdmin}
          selectedRowIds={selectedRowIds}
          onSelectionChange={handleSelectionChange}
          onSelectAll={userIsAdmin ? handleSelectAll : undefined}
        />
      </div>
    </gar-all-cameras>
  );
};

AllCameras.propTypes = {
  userIsAdmin: PropTypes.bool.isRequired,
};
